import { tryCatch } from "@bcmi-labs/art-toolkit/utils"
import type { ARTThemeManager } from "@bcmi-labs/art-ui"

import { staticAssets } from "@/static-assets"
import type { ThemedAssetPath } from "@/types/assets"

/**
 * Resolves the correct asset path based on the provided theme.
 *
 * @param basePath - The base asset path (without .dark suffix)
 * @param theme - The theme to resolve for ("light" or "dark")
 * @returns The resolved asset URL for the specified theme
 *
 * @example
 * ```tsx
 * const currentTheme = useTheme() // or however you get theme
 * const logoSrc = resolveThemedAsset("images/logo.svg", currentTheme)
 *
 * <SomeComponent src={logoSrc} />
 * ```
 */
export function resolveThemedAsset(basePath: ThemedAssetPath, theme: ARTThemeManager["themeName"]): string {
  const baseUrl = staticAssets(basePath)

  if (theme === "dark") {
    const darkVariantUrl = tryCatch(() => staticAssets(getDarkVariantPath(basePath) as ThemedAssetPath))

    return darkVariantUrl.error ? baseUrl : darkVariantUrl.data
  }

  return baseUrl
}

/*
 * ==========================================
 * UTILS
 * ==========================================
 */

/**
 * Helper to construct the dark variant path by inserting .dark before extension
 */
function getDarkVariantPath(path: string): string {
  const lastDotIndex = path.lastIndexOf(".")
  if (lastDotIndex === -1) return path
  return `${path.slice(0, lastDotIndex)}.dark${path.slice(lastDotIndex)}`
}
